/**
 * DiagnosisSection component - Diagnosis dimensions management tab
 *
 * Shows pre-populated dimensions as expandable cards with enable/disable,
 * instruction editing, output config display, and library browser.
 *
 * "Everything should be expandable/inspectable (no black boxes),
 *  the editor should teach you how the system works."
 */

import {
  ChevronDown,
  ChevronRight,
  Library,
  Plus,
  Trash2,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import type { DiagnosisDimension, DiagnosisOutputConfig } from '@/types/template';

import { DiagnosisLibraryDialog } from './DiagnosisLibraryDialog';

// ============================================================================
// TYPES
// ============================================================================

export interface DiagnosisSectionProps {
  dimensions: DiagnosisDimension[];
  onChange: (dims: DiagnosisDimension[]) => void;
  /** @deprecated No longer used for filtering - all dimensions shown */
  mediaType?: 'video' | 'image';
}

// ============================================================================
// HELPERS
// ============================================================================

function outputTypeBadge(config: DiagnosisOutputConfig): string {
  switch (config.type) {
    case 'binary':
      return 'Binary (yes/no)';
    case 'scale':
      return `Scale ${config.scaleMin ?? 1}-${config.scaleMax ?? 5}`;
    case 'options':
      return `Options (${config.options?.length ?? 0})${config.allowMultiple ? ' · multi' : ''}`;
  }
}

function generateTempDimId(): string {
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `dim_custom_${hex}`;
}

const INSTRUCTION_PLACEHOLDER =
  'Analyze: [what to look for]\n' +
  'Consider: [key factors]\n' +
  'High score when: [criteria for high rating]\n' +
  'Low score when: [criteria for low rating]';

// ============================================================================
// DIMENSION CARD
// ============================================================================

interface DimensionCardProps {
  dimension: DiagnosisDimension;
  onUpdate: (dim: DiagnosisDimension) => void;
  onRemove: () => void;
}

function DimensionCard({ dimension, onUpdate, onRemove }: DimensionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasOverrides = !!(dimension.videoInstructions || dimension.imageInstructions);
  const [customizeByMedia, setCustomizeByMedia] = useState(hasOverrides);

  const toggleEnabled = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate({ ...dimension, enabled: !dimension.enabled });
  };

  return (
    <Card
      className={cn(
        'overflow-hidden transition-opacity',
        !dimension.enabled && 'opacity-50',
      )}
    >
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center gap-3 text-left"
      >
        {/* Enable/disable toggle */}
        <span
          role="switch"
          tabIndex={0}
          aria-checked={dimension.enabled}
          onClick={toggleEnabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onUpdate({ ...dimension, enabled: !dimension.enabled });
            }
          }}
          className={cn(
            'flex-shrink-0 w-9 h-5 rounded-full relative cursor-pointer transition-colors',
            dimension.enabled ? 'bg-brand' : 'bg-border-default',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-4 h-4 rounded-full bg-surface shadow-sm transition-transform',
              dimension.enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </span>

        {/* Name */}
        <span
          className={cn(
            'font-semibold text-sm flex-shrink-0',
            dimension.enabled ? 'text-primary' : 'text-muted',
          )}
        >
          {dimension.name}
        </span>

        {/* Output type badge */}
        <span className="bg-surface-subtle text-secondary text-xs px-2 py-0.5 rounded">
          {outputTypeBadge(dimension.outputConfig)}
        </span>

        {/* System badge */}
        {dimension.isSystem && (
          <span className="bg-surface-subtle text-muted text-[10px] font-medium px-2 py-0.5 rounded">
            System
          </span>
        )}

        {/* Media-specific note */}
        {dimension.mediaTypes && dimension.mediaTypes.length === 1 && (
          <span className="text-muted text-[10px] italic">
            Applies to {dimension.mediaTypes[0]} creatives
          </span>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Expand chevron */}
        {expanded ? (
          <ChevronDown size={16} className="text-muted flex-shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-muted flex-shrink-0" />
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border-subtle space-y-4">
          {/* Name (editable for non-system) */}
          {!dimension.isSystem && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-secondary">
                Dimension Name
              </label>
              <input
                type="text"
                value={dimension.name}
                onChange={(e) => onUpdate({ ...dimension, name: e.target.value })}
                maxLength={100}
                className="w-full text-sm bg-surface border border-border-subtle rounded-md px-3 py-2 text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          )}

          {/* Instructions (hidden when customizing by media type for non-system) */}
          {(!customizeByMedia || dimension.isSystem) && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-secondary">
                AI Instructions
              </label>
              <p className="text-xs text-muted mb-2">
                Describe what the AI should analyze and how to interpret it.
              </p>
              <textarea
                value={dimension.instructions}
                onChange={(e) => onUpdate({ ...dimension, instructions: e.target.value })}
                rows={4}
                placeholder={INSTRUCTION_PLACEHOLDER}
                className="w-full text-sm bg-surface border border-border-subtle rounded-md p-3 text-primary placeholder:text-muted resize-y focus:outline-none focus:ring-1 focus:ring-brand leading-relaxed"
                readOnly={dimension.isSystem}
              />
            </div>
          )}

          {/* Customize by media type toggle (non-system only) */}
          {!dimension.isSystem && (
            <div className="pt-2 border-t border-border-subtle">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customizeByMedia}
                  onChange={() => {
                    if (customizeByMedia) {
                      setCustomizeByMedia(false);
                      onUpdate({ ...dimension, videoInstructions: undefined, imageInstructions: undefined });
                    } else {
                      setCustomizeByMedia(true);
                      onUpdate({ ...dimension, videoInstructions: '', imageInstructions: '' });
                    }
                  }}
                  className="w-4 h-4 rounded border-border-default text-brand focus:ring-brand/50"
                />
                <span className="text-xs font-medium text-secondary">
                  Customize by media type
                </span>
                <span className="text-[10px] text-muted">
                  (override instructions for video vs. image)
                </span>
              </label>
            </div>
          )}

          {/* Video/Image-specific instructions (shown when customizing or system with overrides) */}
          {(customizeByMedia || (dimension.isSystem && dimension.videoInstructions)) && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-secondary">
                Video-Specific Instructions
              </label>
              <textarea
                value={dimension.videoInstructions ?? ''}
                onChange={(e) =>
                  onUpdate({ ...dimension, videoInstructions: e.target.value })
                }
                rows={2}
                className="w-full text-sm bg-surface border border-border-subtle rounded-md p-3 text-primary resize-y focus:outline-none focus:ring-1 focus:ring-brand leading-relaxed"
                readOnly={dimension.isSystem}
              />
            </div>
          )}

          {(customizeByMedia || (dimension.isSystem && dimension.imageInstructions)) && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-secondary">
                Image-Specific Instructions
              </label>
              <textarea
                value={dimension.imageInstructions ?? ''}
                onChange={(e) =>
                  onUpdate({ ...dimension, imageInstructions: e.target.value })
                }
                rows={2}
                className="w-full text-sm bg-surface border border-border-subtle rounded-md p-3 text-primary resize-y focus:outline-none focus:ring-1 focus:ring-brand leading-relaxed"
                readOnly={dimension.isSystem}
              />
            </div>
          )}

          {/* Output config */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-secondary">
              Structured Output
            </label>
            {dimension.isSystem ? (
              /* Read-only display for system dimensions */
              <div className="bg-surface-subtle rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-secondary w-20">Type:</span>
                  <span className="text-xs text-primary">{dimension.outputConfig.type}</span>
                </div>
                {dimension.outputConfig.type === 'scale' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-secondary w-20">Range:</span>
                    <span className="text-xs text-primary">
                      {dimension.outputConfig.scaleMin ?? 1} to {dimension.outputConfig.scaleMax ?? 10}
                    </span>
                  </div>
                )}
                {dimension.outputConfig.options && dimension.outputConfig.options.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-secondary w-20">Options:</span>
                    <div className="flex flex-wrap gap-1">
                      {dimension.outputConfig.options.map((opt) => (
                        <span
                          key={opt}
                          className="inline-flex items-center bg-brand/10 text-brand text-xs px-2 py-0.5 rounded-full"
                        >
                          {opt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {dimension.outputConfig.allowMultiple && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-secondary w-20">Multiple:</span>
                    <span className="text-xs text-primary">Yes</span>
                  </div>
                )}
              </div>
            ) : (
              /* Interactive output config for non-system dimensions */
              <div className="bg-surface-subtle rounded-lg p-3 space-y-3">
                {/* Type cards */}
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { type: 'scale' as const, icon: '📊', label: 'Scale', desc: 'AI rates on a numeric scale (e.g. 1-5)' },
                    { type: 'binary' as const, icon: '✓✗', label: 'Binary', desc: 'AI answers Yes or No' },
                    { type: 'options' as const, icon: '☰', label: 'Options', desc: 'AI picks from your defined choices' },
                  ]).map((card) => (
                    <button
                      key={card.type}
                      type="button"
                      onClick={() => {
                        let newConfig: DiagnosisOutputConfig;
                        switch (card.type) {
                          case 'scale':
                            newConfig = { type: 'scale', scaleMin: 1, scaleMax: 5 };
                            break;
                          case 'binary':
                            newConfig = { type: 'binary' };
                            break;
                          case 'options':
                            newConfig = { type: 'options', options: dimension.outputConfig.options ?? [] };
                            break;
                        }
                        onUpdate({ ...dimension, outputConfig: newConfig });
                      }}
                      className={cn(
                        'flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-center transition-all',
                        dimension.outputConfig.type === card.type
                          ? 'border-brand bg-brand/5'
                          : 'border-border-subtle hover:border-brand/30 bg-surface',
                      )}
                    >
                      <span className="text-lg">{card.icon}</span>
                      <span className="text-xs font-semibold text-primary">{card.label}</span>
                      <span className="text-[10px] text-muted leading-tight">{card.desc}</span>
                    </button>
                  ))}
                </div>

                {/* Scale: min/max inputs */}
                {dimension.outputConfig.type === 'scale' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-secondary w-20">Range:</span>
                    <input
                      type="number"
                      value={dimension.outputConfig.scaleMin ?? 1}
                      onChange={(e) =>
                        onUpdate({
                          ...dimension,
                          outputConfig: {
                            ...dimension.outputConfig,
                            scaleMin: Number(e.target.value),
                          },
                        })
                      }
                      className="w-16 bg-surface border border-border-subtle rounded-md text-sm px-2 py-1 text-primary focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                    <span className="text-xs text-muted">to</span>
                    <input
                      type="number"
                      value={dimension.outputConfig.scaleMax ?? 5}
                      onChange={(e) =>
                        onUpdate({
                          ...dimension,
                          outputConfig: {
                            ...dimension.outputConfig,
                            scaleMax: Number(e.target.value),
                          },
                        })
                      }
                      className="w-16 bg-surface border border-border-subtle rounded-md text-sm px-2 py-1 text-primary focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                  </div>
                )}

                {/* Options: tag/chip editor + allow multiple toggle */}
                {dimension.outputConfig.type === 'options' && (
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs font-medium text-secondary mb-1.5 block">Options:</span>
                      {/* Existing options as chips */}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(dimension.outputConfig.options ?? []).map((opt, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 bg-brand/10 text-brand text-xs px-2 py-1 rounded-full"
                          >
                            {opt}
                            <button
                              type="button"
                              onClick={() => {
                                const newOptions = (dimension.outputConfig.options ?? []).filter(
                                  (_, i) => i !== idx,
                                );
                                onUpdate({
                                  ...dimension,
                                  outputConfig: { ...dimension.outputConfig, options: newOptions },
                                });
                              }}
                              className="ml-0.5 text-brand/60 hover:text-brand transition-colors"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                      {/* Input for adding new options */}
                      <input
                        type="text"
                        placeholder="Type an option and press Enter"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = (e.target as HTMLInputElement).value.trim();
                            if (val) {
                              const existing = dimension.outputConfig.options ?? [];
                              if (!existing.includes(val)) {
                                onUpdate({
                                  ...dimension,
                                  outputConfig: {
                                    ...dimension.outputConfig,
                                    options: [...existing, val],
                                  },
                                });
                              }
                              (e.target as HTMLInputElement).value = '';
                            }
                          }
                        }}
                        className="w-full bg-surface border border-border-subtle rounded-md text-sm px-2 py-1.5 text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </div>
                    {/* Allow multiple toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span
                        role="switch"
                        tabIndex={0}
                        aria-checked={dimension.outputConfig.allowMultiple ?? false}
                        onClick={() =>
                          onUpdate({
                            ...dimension,
                            outputConfig: {
                              ...dimension.outputConfig,
                              allowMultiple: !dimension.outputConfig.allowMultiple,
                            },
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onUpdate({
                              ...dimension,
                              outputConfig: {
                                ...dimension.outputConfig,
                                allowMultiple: !dimension.outputConfig.allowMultiple,
                              },
                            });
                          }
                        }}
                        className={cn(
                          'flex-shrink-0 w-8 h-[18px] rounded-full relative cursor-pointer transition-colors',
                          dimension.outputConfig.allowMultiple ? 'bg-brand' : 'bg-border-default',
                        )}
                      >
                        <span
                          className={cn(
                            'absolute top-[1px] w-4 h-4 rounded-full bg-surface shadow-sm transition-transform',
                            dimension.outputConfig.allowMultiple ? 'translate-x-[14px]' : 'translate-x-[1px]',
                          )}
                        />
                      </span>
                      <span className="text-xs text-secondary">Allow multiple selections</span>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Delete button (non-system only) */}
          {!dimension.isSystem && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="text-danger hover:text-danger hover:bg-danger/10"
                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
              >
                Remove Dimension
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function DiagnosisSection({
  dimensions,
  onChange,
  mediaType: _mediaType,
}: DiagnosisSectionProps) {
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Show ALL dimensions (unified templates - no media type filtering)
  const enabledCount = dimensions.filter((d) => d.enabled).length;

  const handleUpdateDimension = useCallback(
    (updated: DiagnosisDimension) => {
      onChange(
        dimensions.map((d) =>
          d.dimensionId === updated.dimensionId ? updated : d,
        ),
      );
    },
    [dimensions, onChange],
  );

  const handleRemoveDimension = useCallback(
    (dimensionId: string) => {
      onChange(dimensions.filter((d) => d.dimensionId !== dimensionId));
    },
    [dimensions, onChange],
  );

  const handleAddFromLibrary = useCallback(
    (newDims: DiagnosisDimension[]) => {
      onChange([...dimensions, ...newDims]);
    },
    [dimensions, onChange],
  );

  const handleAddCustom = useCallback(() => {
    const newDim: DiagnosisDimension = {
      dimensionId: generateTempDimId(),
      name: 'New Dimension',
      instructions: '',
      outputConfig: { type: 'scale', scaleMin: 1, scaleMax: 5 },
      enabled: true,
      isSystem: false,
    };
    onChange([...dimensions, newDim]);
  }, [dimensions, onChange]);

  const existingIds = dimensions.map((d) => d.dimensionId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-primary">
            Diagnosis Dimensions
          </h3>
          <p className="text-xs text-muted mt-0.5">
            {enabledCount} of {dimensions.length} enabled
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLibraryOpen(true)}
            leftIcon={<Library className="h-4 w-4" />}
          >
            Add from Library
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddCustom}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Custom
          </Button>
        </div>
      </div>

      {/* Dimension list */}
      {dimensions.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border-subtle rounded-lg">
          <p className="text-sm text-muted mb-4">
            No diagnosis dimensions configured yet.
          </p>
          <Button
            variant="secondary"
            onClick={() => setLibraryOpen(true)}
            leftIcon={<Library className="h-4 w-4" />}
          >
            Browse Dimension Library
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {dimensions.map((dim) => (
            <DimensionCard
              key={dim.dimensionId}
              dimension={dim}
              onUpdate={handleUpdateDimension}
              onRemove={() => handleRemoveDimension(dim.dimensionId)}
            />
          ))}
        </div>
      )}

      {/* Library dialog */}
      <DiagnosisLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onAdd={handleAddFromLibrary}
        existingDimensionIds={existingIds}
      />
    </div>
  );
}
